import * as THREE from "three";

export type RotationAxisIndex = 0 | 1 | 2;

export const ROTATION_GIZMO_ROOT_NAME = "__rotationGizmoRoot";

const AXIS_COLORS: [number, number, number] = [0xff3f5e, 0x3bd16f, 0x3f8bff];
const BASE_RING_RADIUS = 0.11;
const BASE_RING_TUBE = 0.0019;
const BASE_PICK_TUBE = 0.015;
const BASE_CENTER_RADIUS = 0.0045;
const BASE_MARKER_LENGTH = 0.028;
const BASE_MARKER_WIDTH = 0.013;
const BASE_MARKER_PLANE_OFFSET = 0.002;
const DRAG_ARROW_ANGLE_OFFSET = 0.16; // radians, keeps both arrows near same side
const HOVER_HEAD_SCALE = 1.3;
const INACTIVE_OPACITY = 0.3;
const ACTIVE_OPACITY = 1.0;
const ANIM_LERP_ALPHA = 0.42;

type RingHandle = {
  axis: RotationAxisIndex;
  pivot: THREE.Group; // rotates ring plane normal to +axis
  ring: THREE.Mesh;
  pick: THREE.Mesh;
  head: THREE.Mesh;
  reverseHead: THREE.Mesh;
  ringMat: THREE.MeshBasicMaterial;
  headMat: THREE.MeshBasicMaterial;
  reverseHeadMat: THREE.MeshBasicMaterial;
  currentHeadScale: number;
  targetHeadScale: number;
  currentOpacity: number;
  targetOpacity: number;
};

export type RotationGizmo = {
  root: THREE.Group;
  handles: RingHandle[];
  pickTargets: THREE.Object3D[];
  hoveredAxis: RotationAxisIndex | null;
  draggingAxis: RotationAxisIndex | null;
};

const makeAxisPlaneRotation = (axis: RotationAxisIndex): THREE.Euler => {
  // Torus lies in XY plane by default (normal +Z). Rotate to make normal +X/+Y/+Z.
  if (axis === 0) return new THREE.Euler(0, Math.PI / 2, 0); // +Z -> +X
  if (axis === 1) return new THREE.Euler(-Math.PI / 2, 0, 0); // +Z -> +Y
  return new THREE.Euler(0, 0, 0); // +Z
};

const makeOverlayMaterial = (color: number): THREE.MeshBasicMaterial =>
  new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: ACTIVE_OPACITY,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
    side: THREE.DoubleSide,
  });

const makeRotationChevronGeometry = (): THREE.ShapeGeometry => {
  // Different visual language than translation gizmo: flat chevron marker
  // aligned to ring tangent direction.
  const l = BASE_MARKER_LENGTH;
  const w = BASE_MARKER_WIDTH;
  const shape = new THREE.Shape();
  shape.moveTo(-l * 0.46, -w * 0.5);
  shape.lineTo(l * 0.12, -w * 0.5);
  shape.lineTo(l * 0.38, -w * 0.18);
  shape.lineTo(l * 0.38, w * 0.18);
  shape.lineTo(l * 0.12, w * 0.5);
  shape.lineTo(-l * 0.46, w * 0.5);
  shape.lineTo(-l * 0.16, 0);
  shape.closePath();
  return new THREE.ShapeGeometry(shape, 1);
};

export function createRotationGizmo(): RotationGizmo {
  const root = new THREE.Group();
  root.name = ROTATION_GIZMO_ROOT_NAME;
  root.visible = false;
  root.renderOrder = 9999;
  root.userData.isRotationGizmo = true;

  const handles: RingHandle[] = [];
  const pickTargets: THREE.Object3D[] = [];
  const headGeom = makeRotationChevronGeometry();

  const centerMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.95,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  const center = new THREE.Mesh(new THREE.SphereGeometry(BASE_CENTER_RADIUS, 14, 10), centerMat);
  center.renderOrder = 9999;
  center.userData.isRotationGizmo = true;
  center.userData.rotationGizmoRole = "center";
  root.add(center);

  for (let axis = 0; axis < 3; axis++) {
    const color = AXIS_COLORS[axis];
    const pivot = new THREE.Group();
    pivot.rotation.copy(makeAxisPlaneRotation(axis as RotationAxisIndex));
    pivot.userData.isRotationGizmo = true;
    pivot.userData.rotationGizmoAxis = axis;

    const ringMat = makeOverlayMaterial(color);
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(BASE_RING_RADIUS, BASE_RING_TUBE, 10, 100),
      ringMat
    );
    ring.renderOrder = 9999;
    ring.userData.isRotationGizmo = true;
    ring.userData.rotationGizmoAxis = axis;
    ring.userData.rotationGizmoRole = "ring";

    const pick = new THREE.Mesh(
      new THREE.TorusGeometry(BASE_RING_RADIUS, BASE_PICK_TUBE, 8, 80),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0,
        depthTest: false,
        depthWrite: false,
        visible: true,
      })
    );
    pick.renderOrder = 9999;
    pick.userData.isRotationGizmo = true;
    pick.userData.rotationGizmoAxis = axis;
    pick.userData.rotationGizmoRole = "pick";

    const headMat = makeOverlayMaterial(color);
    const head = new THREE.Mesh(headGeom.clone(), headMat);
    const headTheta = DRAG_ARROW_ANGLE_OFFSET;
    head.position.set(
      BASE_RING_RADIUS * Math.cos(headTheta),
      BASE_RING_RADIUS * Math.sin(headTheta),
      BASE_MARKER_PLANE_OFFSET
    );
    // Chevron points +X by default. Rotate to +tangent direction at theta.
    head.rotation.set(0, 0, headTheta + Math.PI / 2);
    head.renderOrder = 9999;
    head.userData.isRotationGizmo = true;
    head.userData.rotationGizmoAxis = axis;
    head.userData.rotationGizmoRole = "head";

    const reverseHeadMat = makeOverlayMaterial(color);
    const reverseHead = new THREE.Mesh(headGeom.clone(), reverseHeadMat);
    const reverseTheta = -DRAG_ARROW_ANGLE_OFFSET;
    reverseHead.position.set(
      BASE_RING_RADIUS * Math.cos(reverseTheta),
      BASE_RING_RADIUS * Math.sin(reverseTheta),
      BASE_MARKER_PLANE_OFFSET
    );
    // Opposite tangent direction at reverseTheta for clear bidirectional cue.
    reverseHead.rotation.set(0, 0, reverseTheta - Math.PI / 2);
    reverseHead.visible = false;
    reverseHead.renderOrder = 9999;
    reverseHead.userData.isRotationGizmo = true;
    reverseHead.userData.rotationGizmoAxis = axis;
    reverseHead.userData.rotationGizmoRole = "reverseHead";

    pivot.add(pick);
    pivot.add(ring);
    pivot.add(head);
    pivot.add(reverseHead);
    root.add(pivot);

    handles.push({
      axis: axis as RotationAxisIndex,
      pivot,
      ring,
      pick,
      head,
      reverseHead,
      ringMat,
      headMat,
      reverseHeadMat,
      currentHeadScale: 1,
      targetHeadScale: 1,
      currentOpacity: ACTIVE_OPACITY,
      targetOpacity: ACTIVE_OPACITY,
    });
    pickTargets.push(pick);
  }

  return { root, handles, pickTargets, hoveredAxis: null, draggingAxis: null };
}

export function disposeRotationGizmo(gizmo: RotationGizmo): void {
  try {
    gizmo.root.parent?.remove(gizmo.root);
  } catch {
    // ignore
  }
  gizmo.root.traverse((obj) => {
    const mesh = obj as THREE.Object3D & {
      geometry?: THREE.BufferGeometry;
      material?: THREE.Material | THREE.Material[];
    };
    if (mesh.geometry && typeof mesh.geometry.dispose === "function") {
      try {
        mesh.geometry.dispose();
      } catch {
        // ignore
      }
    }
    const mat = mesh.material;
    if (!mat) return;
    if (Array.isArray(mat)) {
      mat.forEach((m) => {
        try {
          m?.dispose?.();
        } catch {
          // ignore
        }
      });
    } else {
      try {
        mat.dispose?.();
      } catch {
        // ignore
      }
    }
  });
}

function applyVisualTargets(gizmo: RotationGizmo): void {
  const hovered = gizmo.hoveredAxis;
  const dragging = gizmo.draggingAxis;
  for (const handle of gizmo.handles) {
    const isDraggingAxis = dragging != null && dragging === handle.axis;
    const isHoveredOnly = dragging == null && hovered === handle.axis;
    handle.targetHeadScale = isHoveredOnly || isDraggingAxis ? HOVER_HEAD_SCALE : 1;
    if (dragging != null) {
      handle.targetOpacity = isDraggingAxis ? ACTIVE_OPACITY : 0;
      handle.reverseHead.visible = false;
    } else {
      handle.targetOpacity =
        hovered == null ? ACTIVE_OPACITY : isHoveredOnly ? ACTIVE_OPACITY : INACTIVE_OPACITY;
      handle.reverseHead.visible = false;
    }
  }
}

export function setHoveredRotationAxis(
  gizmo: RotationGizmo,
  hovered: RotationAxisIndex | null
): void {
  gizmo.hoveredAxis = hovered;
  applyVisualTargets(gizmo);
}

export function setDraggingRotationAxis(
  gizmo: RotationGizmo,
  dragging: RotationAxisIndex | null
): void {
  gizmo.draggingAxis = dragging;
  if (dragging != null) gizmo.hoveredAxis = dragging;
  applyVisualTargets(gizmo);
}

export function updateRotationGizmoTransform(
  gizmo: RotationGizmo,
  origin: THREE.Vector3,
  parentWorldQuat: THREE.Quaternion,
  cameraDistance: number
): void {
  for (const handle of gizmo.handles) {
    handle.currentHeadScale = THREE.MathUtils.lerp(
      handle.currentHeadScale,
      handle.targetHeadScale,
      ANIM_LERP_ALPHA
    );
    handle.currentOpacity = THREE.MathUtils.lerp(
      handle.currentOpacity,
      handle.targetOpacity,
      ANIM_LERP_ALPHA
    );
    if (Math.abs(handle.currentHeadScale - handle.targetHeadScale) < 0.0015) {
      handle.currentHeadScale = handle.targetHeadScale;
    }
    if (Math.abs(handle.currentOpacity - handle.targetOpacity) < 0.0015) {
      handle.currentOpacity = handle.targetOpacity;
    }
    handle.head.scale.setScalar(handle.currentHeadScale);
    handle.reverseHead.scale.setScalar(handle.currentHeadScale);
    handle.ringMat.opacity = handle.currentOpacity;
    handle.headMat.opacity = handle.currentOpacity;
    handle.reverseHeadMat.opacity = handle.currentOpacity;
  }

  gizmo.root.position.copy(origin);
  gizmo.root.quaternion.copy(parentWorldQuat);
  const s = Math.max(0.05, cameraDistance) * 0.64;
  gizmo.root.scale.setScalar(s);
  gizmo.root.updateMatrixWorld(true);
}

export function getRotationAxisWorldDirection(
  gizmo: RotationGizmo,
  axis: RotationAxisIndex,
  out: THREE.Vector3
): THREE.Vector3 {
  const basis = new THREE.Vector3();
  if (axis === 0) basis.set(1, 0, 0);
  else if (axis === 1) basis.set(0, 1, 0);
  else basis.set(0, 0, 1);
  basis.applyQuaternion(gizmo.root.quaternion);
  return out.copy(basis).normalize();
}
